<script lang="ts">
  import { router } from '@inertiajs/svelte'

  interface ReportModalProps {
    isOpen: boolean
    targetType: 'post' | 'comment'
    targetId: string
    postId?: string
    onClose: () => void
  }

  let { isOpen, targetType, targetId, postId, onClose }: ReportModalProps = $props()

  const REASONS = [
    { value: 'spam_commercial',    label: '반복 홍보, 상업적 게시물' },
    { value: 'personal_info',      label: '실명, 연락처, 계좌번호, 주민번호 등 개인정보 노출' },
    { value: 'illegal_content',    label: '마약/도박/불법 판매, 저작권 침해' },
    { value: 'csam',               label: '아동·청소년 성착취물(CSAM)' },
    { value: 'adult_content',      label: '일반 성인물, 선정적 콘텐츠' },
    { value: 'hate_harassment',    label: '차별/모욕/인신공격, 따돌림/괴롭힘' },
    { value: 'violence_self_harm', label: '폭력 위협, 자해/자살 관련 위험 발언' },
    { value: 'false_info',         label: '채용 사기, 허위 구인구직, 잘못된 기술 정보 등' },
    { value: 'other',              label: '위 사유에 해당하지 않는 경우' },
  ]

  const URGENT_REASONS = ['csam', 'violence_self_harm']

  let selectedReason = $state('')
  let description = $state('')
  let isSubmitting = $state(false)

  let descLength = $derived(description.length)
  let isUrgent = $derived(URGENT_REASONS.includes(selectedReason))
  let canSubmit = $derived(
    selectedReason !== '' &&
    descLength >= 20 &&
    descLength <= 500 &&
    !isSubmitting
  )

  let reportUrl = $derived(
    targetType === 'comment'
      ? `/posts/${postId}/comments/${targetId}/reports`
      : `/posts/${targetId}/reports`
  )

  function submit() {
    if (!canSubmit) return
    isSubmitting = true

    router.post(reportUrl, {
      report: { reason: selectedReason, description }
    }, {
      onSuccess: () => {
        onClose()
        selectedReason = ''
        description = ''
      },
      onError: () => { isSubmitting = false },
      onFinish: () => { isSubmitting = false }
    })
  }

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) onClose()
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose()
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onclick={handleBackdropClick}
    onkeydown={handleKeydown}
  >
    <div class="card card-bordered w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-default shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-error/10 flex items-center justify-center">
            <svg class="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h2 class="text-title text-content">콘텐츠 신고</h2>
        </div>
        <button
          onclick={onClose}
          class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-medium hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
          aria-label="닫기"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="p-6 overflow-y-auto flex-1 space-y-5">
        <!-- 신고 사유 -->
        <div>
          <h3 class="text-label font-semibold text-gray-700 dark:text-dark-text-medium uppercase mb-3">
            신고 사유 <span class="text-error">*</span>
          </h3>
          <div class="space-y-2">
            {#each REASONS as reason}
              <label
                class={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  selectedReason === reason.value
                    ? 'border-primary bg-primary-50 dark:bg-primary/10 dark:border-primary/60'
                    : 'border-default hover:border-gray-300 dark:hover:border-dark-text-disabled hover:bg-gray-50 dark:hover:bg-dark-tertiary'
                } ${
                  (reason.value === 'csam' || reason.value === 'violence_self_harm')
                    ? 'hover:border-error/50'
                    : ''
                }`}
              >
                <input
                  type="radio"
                  name="report_reason"
                  value={reason.value}
                  bind:group={selectedReason}
                  class="mt-0.5 shrink-0 accent-primary"
                />
                <span class={`text-body2 leading-snug ${
                  reason.value === 'csam' || reason.value === 'violence_self_harm'
                    ? 'text-error font-medium'
                    : 'text-gray-700 dark:text-dark-text-medium'
                }`}>
                  {reason.label}
                  {#if reason.value === 'csam' || reason.value === 'violence_self_harm'}
                    <span class="ml-1.5 text-caption font-normal bg-error/10 text-error px-1.5 py-0.5 rounded">긴급</span>
                  {/if}
                </span>
              </label>
            {/each}
          </div>
        </div>

        <!-- 긴급 사유 안내 배너 -->
        {#if isUrgent}
          <div class="flex items-start gap-3 p-4 bg-error/8 border border-error/30 rounded-xl">
            <svg class="w-5 h-5 text-error shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
            </svg>
            <div>
              <p class="text-body2 font-semibold text-error">긴급 처리 대상</p>
              <p class="text-caption text-error/80 mt-0.5">
                이 신고 사유는 관련 법령에 따라 <strong>24시간 내 긴급 처리</strong>됩니다.
                (청소년성보호법 §17 / 정보통신망법 §44)
              </p>
            </div>
          </div>
        {/if}

        <!-- 상세 설명 -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-label font-semibold text-gray-700 dark:text-dark-text-medium uppercase">
              추가 설명 <span class="text-error">*</span>
            </h3>
            <span class={`text-caption font-mono ${
              descLength < 20
                ? 'text-error'
                : descLength > 450
                  ? 'text-warning-dark dark:text-warning'
                  : 'text-content-tertiary'
            }`}>
              {descLength} / 500
            </span>
          </div>
          <textarea
            bind:value={description}
            rows="4"
            placeholder="신고 내용을 구체적으로 작성해주세요. (최소 20자)"
            class="textarea resize-none w-full"
          ></textarea>
          {#if descLength > 0 && descLength < 20}
            <p class="text-caption text-error mt-1.5">최소 20자 이상 작성해주세요. ({20 - descLength}자 더 필요)</p>
          {/if}
        </div>

        <!-- 안내 문구 -->
        <p class="text-caption text-gray-400 dark:text-dark-text-disabled leading-relaxed">
          허위 신고는 서비스 이용이 제한될 수 있습니다. 신고자 정보는 피신고자에게 공개되지 않습니다.
        </p>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 p-6 border-t border-default shrink-0">
        <button onclick={onClose} class="btn btn-secondary btn-md">
          취소
        </button>
        <button
          onclick={submit}
          disabled={!canSubmit}
          class="btn btn-danger btn-md disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {#if isSubmitting}
            <svg class="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            신고 중...
          {:else}
            신고하기
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}
